import { format, formatDistanceToNowStrict } from "date-fns";
import { Bell, BellRing, CheckCircle2, ExternalLink, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { InboxNotificationEvent } from "@/hooks/useInboxNotifications";
import { cn } from "@/lib/utils";

interface Props {
  history: InboxNotificationEvent[];
  permission: NotificationPermission | "unsupported";
  freshCount: number;
  onRequestPermission: () => void;
  onDeleteNotification: (inboxItemId: string) => void;
  onOpenInboxItem: (inboxItemId: string) => void;
}

export function InboxNotificationsMenu({
  history,
  permission,
  freshCount,
  onRequestPermission,
  onDeleteNotification,
  onOpenInboxItem,
}: Props) {
  const enabled = permission === "granted";
  const blocked = permission === "denied";
  const unsupported = permission === "unsupported";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "relative size-8 p-0",
            freshCount > 0 && "border-amber-400/60 text-amber-200",
          )}
          title="Inbox notifications"
          aria-label="Inbox notifications"
        >
          {freshCount > 0 ? <BellRing /> : <Bell />}
          {freshCount > 0 ? (
            <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-amber-400 px-1 text-center text-[10px] font-semibold text-black">
              {freshCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="border-b border-border px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium">Notification history</div>
              <div className="text-xs text-muted-fg">
                {enabled
                  ? "Browser notifications enabled"
                  : blocked
                    ? "Browser notifications blocked"
                    : unsupported
                      ? "Browser notifications unsupported"
                      : "Browser notifications not enabled"}
              </div>
            </div>
          </div>
          {!enabled && !blocked && !unsupported ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-2 w-full justify-center"
              onClick={onRequestPermission}
            >
              <Bell />
              Enable browser notifications
            </Button>
          ) : null}
          {enabled ? (
            <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-300">
              <CheckCircle2 className="size-3.5" />
              New inbox items will notify this browser.
            </div>
          ) : null}
        </div>
        <div className="max-h-[420px] overflow-auto">
          {history.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-muted-fg">
              No new-inbox notifications yet.
            </div>
          ) : (
            history.map((event) => (
              <NotificationRow
                key={event.id}
                event={event}
                onDelete={() => onDeleteNotification(event.id)}
                onOpenInboxItem={() => onOpenInboxItem(event.id)}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotificationRow({
  event,
  onDelete,
  onOpenInboxItem,
}: {
  event: InboxNotificationEvent;
  onDelete: () => void;
  onOpenInboxItem: () => void;
}) {
  const timestamp = new Date(event.itemTimestamp);
  const age = formatDistanceToNowStrict(timestamp, { addSuffix: true });
  return (
    <div className="border-b border-border/60 px-3 py-2 last:border-b-0">
      <div className="flex items-center gap-2 text-[11px] text-muted-fg">
        <button
          type="button"
          className="truncate text-left font-medium text-fg hover:text-accent"
          onClick={onOpenInboxItem}
          title="Show this item in the inbox"
        >
          {event.sourceId}
        </button>
        <time
          className="ml-auto shrink-0 tabular-nums"
          dateTime={event.itemTimestamp}
          title={format(timestamp, "PPpp")}
        >
          {age}
        </time>
        <Button
          variant="outline"
          size="sm"
          className="size-7 p-0"
          onClick={onDelete}
          title="Delete notification"
        >
          <Trash2 />
        </Button>
      </div>
      {event.symbols.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {event.symbols.slice(0, 6).map((symbol) => (
            <span
              key={symbol}
              className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-fg"
            >
              {symbol}
            </span>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className="mt-1 block w-full whitespace-pre-wrap break-words text-left text-xs leading-relaxed text-muted-fg hover:text-fg"
        onClick={onOpenInboxItem}
      >
        {event.body || "(no text)"}
      </button>
      {event.pageUrl ? (
        <a
          href={event.pageUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex max-w-full items-center gap-1 text-xs text-accent hover:underline"
        >
          <span className="truncate">Open source page</span>
          <ExternalLink className="size-3 shrink-0" />
        </a>
      ) : null}
    </div>
  );
}
